import React, { useState } from "react";
import { motion } from "framer-motion";
import { Ticket, X } from "lucide-react";
import fr from "@/content/fr";
import { trackInitiateCheckout } from "@/lib/analytics";

// === TICKETING FLAG ===
// When the Zeffy embed is live, flip ENABLE_TICKETING in <TicketingSection /> to true.
// The CTA below will then scroll to / open the ticketing section instead of opening
// the "soon" modal. See `/components/TicketingSection.jsx`.
const ENABLE_TICKETING = false;

const fadeUp = {
  hidden: { y: 22, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.7, ease: "easeOut" } },
};

export const ExpoSection = () => {
  const [openSoon, setOpenSoon] = useState(false);

  const onReserve = () => {
    trackInitiateCheckout({ content_name: "expo_streetwear_sneaker" });
    if (ENABLE_TICKETING) {
      const el = document.getElementById("ticketing");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      setOpenSoon(true);
    }
  };

  return (
    <section
      id="expo"
      data-testid="expo-section"
      className="relative py-24 sm:py-32 lg:py-40 overflow-hidden"
    >
      {/* Background fade */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, #15042E 0%, #1B0840 35%, #15042E 100%)",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-20 right-[-10%] w-[520px] h-[520px] rounded-full blur-3xl opacity-30"
        style={{ background: "radial-gradient(closest-side, rgba(233,30,99,0.5), transparent)" }}
      />

      <div className="relative max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Visual */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            variants={fadeUp}
            className="lg:col-span-6 order-2 lg:order-1"
          >
            <div className="relative">
              {/* Glass frame */}
              <div className="relative rounded-[28px] p-1.5 bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
                <img
                  src="/images/expo-models.png"
                  alt="Modèles Gen Z en streetwear sous néons violets et roses — Expo Streetwear & Sneaker Culture"
                  className="w-full aspect-[4/5] object-cover rounded-[22px]"
                  loading="lazy"
                  draggable="false"
                />
              </div>

              {/* Sneaker accent (Sneaker Culture) */}
              <div className="absolute -top-5 -right-3 sm:-right-6 w-24 sm:w-32 rounded-2xl overflow-hidden border border-white/15 shadow-2xl rotate-[4deg] bg-[#15042E]">
                <img
                  src="/images/sneakers.png"
                  alt="Sneaker de collection sous néons"
                  className="w-full aspect-square object-cover"
                  loading="lazy"
                  draggable="false"
                />
              </div>

              {/* Floating tag */}
              <div className="absolute -bottom-5 -left-3 sm:-left-5 px-4 py-3 rounded-2xl bg-[#15042E]/85 backdrop-blur-xl border border-white/10 shadow-xl">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/55 font-mono">
                  Édition
                </p>
                <p className="font-display font-extrabold text-white text-xl leading-none mt-1">
                  Automne 2026
                </p>
              </div>
            </div>
          </motion.div>

          {/* Copy */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <motion.p
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={fadeUp}
              className="text-[11px] sm:text-xs tracking-[0.34em] uppercase text-[#E91E63] font-mono"
              data-testid="expo-eyebrow"
            >
              {fr.expo.eyebrow}
            </motion.p>

            <motion.h2
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={fadeUp}
              className="font-display font-extrabold text-white uppercase mt-4 leading-[0.96] tracking-[-0.01em]"
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}
              data-testid="expo-title"
            >
              {fr.expo.title}
            </motion.h2>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={fadeUp}
              className="mt-6 inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white/[0.04] border border-white/[0.10] backdrop-blur-md"
              data-testid="expo-date-line"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#E91E63] shadow-[0_0_12px_#E91E63]" />
              <span className="text-[12.5px] tracking-[0.2em] uppercase text-white/85 font-medium">
                {fr.expo.dateLine}
              </span>
            </motion.div>

            <motion.p
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={fadeUp}
              className="mt-7 text-[15.5px] sm:text-[16.5px] leading-[1.75] text-white/72 max-w-[640px]"
              data-testid="expo-body"
            >
              {fr.expo.body}
            </motion.p>

            {/* Pillars */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={fadeUp}
              className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-4"
            >
              {fr.expo.pillars.map((p) => (
                <div
                  key={p.label}
                  className="rounded-2xl p-4 bg-white/[0.03] border border-white/[0.08] backdrop-blur-md"
                >
                  <p className="font-display font-extrabold text-white text-3xl leading-none">
                    {p.value}
                  </p>
                  <p className="mt-2 text-[11px] tracking-[0.18em] uppercase text-white/55 font-mono">
                    {p.label}
                  </p>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.4 }}
              variants={fadeUp}
              className="mt-10"
            >
              <button
                onClick={onReserve}
                data-testid="expo-cta-reserve"
                className="group inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-[#E91E63] hover:bg-[#ff2974] text-white font-display font-extrabold uppercase tracking-[0.12em] text-[15px] shadow-[0_0_0_0_rgba(233,30,99,0.0)] hover:shadow-[0_0_44px_4px_rgba(233,30,99,0.55)] transition-all duration-300 hover:-translate-y-[2px]"
              >
                <Ticket size={18} />
                {fr.expo.cta}
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* "Soon" modal */}
      {openSoon && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-5"
          role="dialog"
          aria-modal="true"
          aria-labelledby="soon-title"
          data-testid="soon-modal"
        >
          <div
            className="absolute inset-0 bg-[#0A0118]/80 backdrop-blur-md"
            onClick={() => setOpenSoon(false)}
          />
          <motion.div
            initial={{ y: 20, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="relative max-w-md w-full rounded-3xl p-7 sm:p-8 bg-gradient-to-br from-[#2A0A52] to-[#15042E] border border-white/10 shadow-2xl"
          >
            <button
              type="button"
              onClick={() => setOpenSoon(false)}
              aria-label="Fermer"
              data-testid="soon-modal-close-x"
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-white/65 hover:text-white hover:bg-white/10 transition"
            >
              <X size={18} />
            </button>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#E91E63] font-mono">
              Billetterie
            </p>
            <h3
              id="soon-title"
              className="mt-3 font-display font-extrabold text-white text-2xl sm:text-3xl uppercase leading-tight"
            >
              {fr.expo.soonModalTitle}
            </h3>
            <p className="mt-4 text-white/72 text-[15px] leading-[1.65]">
              {fr.expo.soonModalBody}
            </p>
            <button
              onClick={() => setOpenSoon(false)}
              data-testid="soon-modal-close"
              className="mt-6 inline-flex items-center justify-center h-11 px-6 rounded-full bg-white text-[#15042E] font-display font-bold uppercase tracking-[0.14em] text-xs hover:bg-white/90 transition"
            >
              {fr.expo.soonModalClose}
            </button>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default ExpoSection;
